import * as React from 'react';
import { useMemo, useState } from 'react';
import { Archive, Edit } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { usePermissions } from '@klareospace/pharos-lib-front-utils';
import type { DataTableColumn, RowAction } from '@klareospace/pharos-lib-web-utils';
import {
  createActionsColumn,
  DataTable,
  RowActionsMenu,
  useDataTablePagination,
} from '@klareospace/pharos-lib-web-utils';

import type { CreateOrderType, OrderType, UpdateOrderType } from '@klareospace/pharos-lib-types';

import { useUpdateOrderType } from '../hooks';
import { ArchiveOrderTypeDialog } from './archive-order-type-dialog';
import { OrderTypeDrawer } from './order-type-drawer';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface OrderTypesTableProps {
  isLoading: boolean;
  orderTypes: OrderType[];
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function useOrderTypeLabel(): (orderType: OrderType) => string {
  const { t } = useTranslation('settings');

  return (orderType: OrderType): string => {
    if (orderType.isSystemic) {
      const key = `orderTypes.systemicLabels.${orderType.code}`;
      const translated = t(key);

      if (translated !== key) {
        return translated;
      }
    }

    return orderType.label;
  };
}

// ─── Component ────────────────────────────────────────────────────────────────

export function OrderTypesTable({ isLoading, orderTypes }: OrderTypesTableProps): React.JSX.Element {
  const { t: tCommon } = useTranslation('common');
  const { t } = useTranslation('settings');
  const resolveLabel = useOrderTypeLabel();
  const { hasPermission } = usePermissions();
  const canArchive = hasPermission('order-types:manage');
  const canWrite = hasPermission('order-types:manage');

  const [editingOrderType, setEditingOrderType] = useState<OrderType | null>(null);
  const [archiveTarget, setArchiveTarget] = useState<string>('');

  const updateMutation = useUpdateOrderType();

  async function handleUpdateSubmit(data: CreateOrderType | UpdateOrderType): Promise<void> {
    if (!editingOrderType) return;
    await updateMutation.mutateAsync({ data: { label: data.label }, id: editingOrderType.id });
  }

  const getRowActions = useMemo(
    () =>
      (orderType: OrderType): RowAction[] => {
        const actions: RowAction[] = [];

        if (canWrite) {
          actions.push({
            icon: <Edit className="h-4 w-4" />,
            label: tCommon('actions.edit'),
            onClick: (): void => setEditingOrderType(orderType),
          });
        }

        if (canArchive) {
          actions.push({
            className: 'text-red-600',
            hasSeparator: true,
            icon: <Archive className="h-4 w-4" />,
            label: tCommon('actions.archive'),
            onClick: (): void => setArchiveTarget(orderType.id),
          });
        }

        return actions;
      },
    [canArchive, canWrite, t],
  );

  const columns = useMemo<DataTableColumn<OrderType>[]>(
    () => [
      {
        cell: (orderType): React.ReactNode => <span className="font-medium">{resolveLabel(orderType)}</span>,
        cellClassName: 'truncate',
        className: 'w-[50%] min-w-[200px]',
        header: tCommon('fields.label'),
        key: 'label',
      },
      {
        cell: (orderType): React.ReactNode => (
          <span className="font-mono text-xs text-muted-foreground">{orderType.code}</span>
        ),
        cellClassName: 'truncate',
        className: 'w-[40%] min-w-[120px]',
        header: tCommon('fields.code'),
        key: 'code',
      },
      createActionsColumn<OrderType>((orderType) => {
        if (orderType.isSystemic) return null;
        const actions = getRowActions(orderType);
        if (actions.length === 0) return null;
        return <RowActionsMenu actions={actions} ariaLabel={tCommon('table.actionsHeader')} />;
      }),
    ],
    [getRowActions, resolveLabel, t],
  );

  const pagination = useDataTablePagination({
    labels: {
      nextPage: tCommon('pagination.nextPage'),
      pageLabel: (current, total): string => tCommon('pagination.pageLabel', { current, total }),
      previousPage: tCommon('pagination.previousPage'),
      showingText: (): string => '',
    },
    pagination: {
      currentPage: 1,
      onPageChange: (): void => {},
      pageSize: orderTypes.length,
      totalItems: orderTypes.length,
      totalPages: 1,
    },
  });

  return (
    <>
      <DataTable<OrderType>
        ariaLabel={t('orderTypes.table.ariaLabel')}
        columns={columns}
        emptyMessage={t('orderTypes.table.empty')}
        isLoading={isLoading}
        pagination={pagination}
        rowKey={(orderType): string => orderType.id}
        rows={orderTypes}
      />
      <OrderTypeDrawer
        isPending={updateMutation.isPending}
        onClose={(): void => setEditingOrderType(null)}
        onSubmit={handleUpdateSubmit}
        open={editingOrderType !== null}
        orderType={editingOrderType}
      />
      <ArchiveOrderTypeDialog onClose={(): void => setArchiveTarget('')} orderTypeId={archiveTarget} />
    </>
  );
}
